import React, { useEffect } from "react";
import Input from "../../../form/Input";
import Select from "../../../form/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import useMutation from "../../../../hooks/useMutation";
import Radio from "../../../form/Radio";
import { questionAddEdit } from "../../../../services/questionService";
import { classNames } from "../../../../helpers/classNames";

const validation = {
  title: { required: true, message: "Question title is required" },
  options: {
    required: true,
    type: "arrayValidation",
    validationField: ["title"],
  },
};

const ManageQuestion = ({ getResponse = () => {}, close = () => {}, data }) => {
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      answerType: "multi",
      options: [
        { title: "", correct: true },
        { title: "", correct: false },
      ],
    },
  });

  useEffect(() => {
    if (data?._id) {
      setFieldsValue(data);
    }
  }, [data]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      return await questionAddEdit({
        ...val,
        ...(data?._id && { id: data?._id }),
      });
    },
    onSuccess: (res) => {
      getResponse(res);
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4">
        <div className="relative col-span-4">
          <Input
            label={"Add Question"}
            inputPlaceholder={"Enter Add Question"}
            inputName="title"
            value={values?.title || ""}
            onChange={handleChange}
            {...formValidate(errors, "title")}
          />
        </div>
        <div className="relative col-span-4">
          <Select
            label={"Answer Type"}
            placeholder="Multiple Choice, Yes/No,"
            selectedValue={values?.answerType}
            dropdownData={[
              { name: "Multiple Choice", value: "multi" },
              { name: "Yes/No", value: "single" },
            ]}
            setSelectedValue={(val) => {
              if (val === "single") {
                setFieldsValue({
                  answerType: val,
                  options: [
                    { title: "Yes", correct: true },
                    { title: "No", correct: false },
                  ],
                });
              } else {
                handleChange({
                  target: { name: "answerType", value: val },
                });
                setFieldsValue({
                  answerType: val,
                  options: [
                    { title: "", correct: true },
                    { title: "", correct: false },
                  ],
                });
              }
            }}
          />
        </div>

        <div className="relative col-span-4 space-y-2">
          <div
            className={
              "text-sm font-medium text-shades-07 mb-1 font-CanelaDeck pb-1 border-b border-shades-05/50"
            }
          >
            Options
          </div>
          {values?.options?.length > 0 && (
            <div className="space-y-2">
              {values?.options?.map((option, i) => (
                <div key={option?._id || i} className="flex gap-3">
                  {values?.answerType === "multi" && (
                    <>
                      <Input
                        label={`Option ${i + 1}`}
                        inputPlaceholder={"Enter option value"}
                        value={option?.title}
                        inputName={"title"}
                        onChange={(e) => {
                          setFieldsValue({
                            options: values?.options?.map((item, index) =>
                              index === i
                                ? { ...item, [e.target.name]: e.target.value }
                                : item
                            ),
                          });
                        }}
                        {...formValidate(errors, "options", i, "title")}
                      />
                    </>
                  )}
                  {values?.answerType === "single" && (
                    <div>{option?.title}</div>
                  )}
                  <div
                    className={classNames(
                      "flex items-center justify-center",
                      values?.answerType === "single" ? "" : "pt-7"
                    )}
                  >
                    <Radio
                      radioName={"correct"}
                      isChecked={option?.correct}
                      onChange={(e) => {
                        setFieldsValue({
                          options: values?.options?.map((item, index) =>
                            index === i
                              ? { ...item, [e.target.name]: e.target.checked }
                              : { ...item, [e.target.name]: false }
                          ),
                        });
                      }}
                    />
                  </div>
                  {values?.options?.length > 2 && (
                    <div className="pt-7 flex items-center justify-center">
                      <Button
                        buttonIcon={"fa-regular fa-trash-can"}
                        buttonClasses={
                          "!h-6 !px-1 !bg-transparent !text-chestnut-rose"
                        }
                        buttonIconPosition={"right"}
                        buttonFunction={() => {
                          setFieldsValue({
                            options: (values?.options || [])?.filter(
                              (item, index) => i !== index
                            ),
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {values?.answerType === "multi" && (
            <div>
              <Button
                buttonLabel={"Add More Option"}
                buttonClasses={
                  "!rounded-full !h-8 !border-0 !bg-transparent !text-chestnut-rose !px-0 !rounded-none"
                }
                buttonLabelClasses={"mx-auto !text-sm"}
                buttonFunction={() => {
                  setFieldsValue({
                    options: [
                      ...values?.options,
                      { title: "", correct: false },
                    ],
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={"Save Question"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageQuestion;
